import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { articles } from "@/mocks/articles";
import PageSEO from "@/components/feature/PageSEO";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://oopbuyspreadsheets.mom";

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

function parseDateToISO(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "2026-06-22";
  return d.toISOString().split("T")[0];
}

const articleMeta: Record<string, { description: string; keywords: string }> = {
  "oopbuy-vs-google-sheets": {
    description:
      "Detailed comparison of OOPBUY Spreadsheet vs Google Sheets for product sourcing. Discover which spreadsheet tool offers better templates, automation, and e-commerce features for online sellers.",
    keywords:
      "oopbuy vs google sheets, oopbuy spreadsheet comparison, google sheets alternative, product sourcing spreadsheet, ecommerce spreadsheet tool, supplier management",
  },
  "oopbuy-vs-excel": {
    description:
      "OOPBUY Spreadsheet vs Excel compared for e-commerce sellers. See why purpose-built sourcing templates beat generic spreadsheet tools for supplier management and profit tracking.",
    keywords:
      "oopbuy vs excel, oopbuy spreadsheet comparison, excel alternative for sourcing, ecommerce supplier management, spreadsheet automation tool",
  },
  "oopbuy-vs-airtable": {
    description:
      "Compare OOPBUY Spreadsheet vs Airtable for inventory management. Learn which platform offers better sourcing workflows, category templates, and pricing for online businesses.",
    keywords:
      "oopbuy vs airtable, airtable alternative, inventory management tool, sourcing workflow platform, ecommerce database spreadsheet",
  },
  "best-spreadsheet-tools-2026": {
    description:
      "Complete ranking of the best spreadsheet tools for e-commerce in 2026. OOPBUY Spreadsheet, Google Sheets, Excel, and Airtable compared for sourcing and supplier management.",
    keywords:
      "best spreadsheet tools 2026, top spreadsheet software, ecommerce tools ranking, sourcing software comparison, best sourcing tool",
  },
  "oopbuy-review": {
    description:
      "Honest OOPBUY Spreadsheet review covering features, pricing, pros and cons. Is this sourcing tool worth it for your online business? Read our in-depth analysis.",
    keywords:
      "oopbuy spreadsheet review, oopbuy review 2026, is oopbuy worth it, sourcing tool review, ecommerce spreadsheet review, oopbuy pros and cons",
  },
  "oopbuy-for-ecommerce": {
    description:
      "Practical guide to using OOPBUY Spreadsheet for e-commerce product sourcing. Set up supplier databases, run comparisons, and track profit margins efficiently.",
    keywords:
      "oopbuy for ecommerce, ecommerce product sourcing, supplier management tool, online store spreadsheet, product sourcing guide",
  },
  "oopbuy-for-dropshipping": {
    description:
      "How to use OOPBUY Spreadsheet for dropshipping supply chain management. Manage multiple suppliers, track profits across platforms, and automate your workflow.",
    keywords:
      "oopbuy for dropshipping, dropshipping supply chain, dropshipping supplier management, multi-platform profit tracking, dropshipping automation tool",
  },
  "oopbuy-beginner-guide": {
    description:
      "Step-by-step OOPBUY Spreadsheet beginner guide for new sellers. Learn to choose templates, enter supplier data, run comparisons, and track profits from day one.",
    keywords:
      "oopbuy beginner guide, oopbuy spreadsheet tutorial, new seller sourcing guide, how to use oopbuy, sourcing spreadsheet tutorial",
  },
  "is-oopbuy-safe": {
    description:
      "Is OOPBUY Spreadsheet safe? Complete security, privacy, and data protection review. Enterprise-grade encryption, access controls, and compliance explained.",
    keywords:
      "is oopbuy safe, oopbuy spreadsheet security, oopbuy data protection, spreadsheet security review, cloud sourcing tool privacy",
  },
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? articles[slug] : undefined;

  if (!article) {
    return (
      <main className="bg-background-50 min-h-screen flex items-center justify-center">
        <PageSEO
          title="Article Not Found — OOPBUY Spreadsheet"
          description="The article you are looking for does not exist or has been moved. Browse our complete library of sourcing guides, comparisons, and reviews."
          keywords="oopbuy spreadsheet, article not found"
          canonicalPath={`/article/${slug || ""}`}
          ogType="website"
          schemas={[]}
        />
        <div className="text-center px-4">
          <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-500 mb-6">The article you are looking for does not exist or has been moved.</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </main>
    );
  }

  const meta = articleMeta[article.slug] || {
    description: stripHtml(article.intro).substring(0, 155),
    keywords: "oopbuy spreadsheet, spreadsheet automation, sourcing tool",
  };

  const publishedTime = parseDateToISO(article.date);
  const canonicalPath = `/article/${article.slug}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": meta.description,
    "image": article.imageUrl,
    "datePublished": publishedTime,
    "dateModified": "2026-06-22",
    "author": {
      "@type": "Organization",
      "name": "OOPBUY Spreadsheet",
    },
    "publisher": {
      "@type": "Organization",
      "name": "OOPBUY Spreadsheet",
      "url": siteUrl,
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}${canonicalPath}`,
    },
  };

  return (
    <main className="bg-background-50 min-h-screen">
      <PageSEO
        title={`${article.title} — OOPBUY Spreadsheet`}
        description={meta.description}
        keywords={meta.keywords}
        canonicalPath={canonicalPath}
        ogType="article"
        ogImage={article.imageUrl}
        publishedTime={publishedTime}
        modifiedTime="2026-06-22"
        schemas={[articleSchema]}
      />
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap transition-colors mb-10"
        >
          <i className="ri-arrow-left-line"></i>
          <span>Back to Home</span>
        </button>

        <article>
          <header className="mb-12">
            <span className="text-xs font-medium text-accent-700 tracking-wider uppercase">
              {article.category}
            </span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mt-3 mb-6 leading-[1.15]">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
          </header>

          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full aspect-[2/1] object-cover"
            />
          </div>

          <div className="space-y-6 text-foreground-700 leading-relaxed text-sm md:text-base">
            <p
              className="text-lg md:text-xl text-foreground-900 font-medium leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.intro }}
            />

            {article.sections.map((section, i) => (
              <div key={i}>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="mb-4" dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 text-center">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-950 mb-3">
                {article.ctaTitle}
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                {article.ctaText}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors"
                >
                  Try OOPBUY Spreadsheet Now
                </a>
                <button
                  onClick={() => navigate("/")}
                  className="px-6 py-3 rounded-full border border-background-200 text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-background-200/50 transition-colors"
                >
                  Back to OOPBUY Home
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>
    </main>
  );
}